import React from 'react';
import { Link } from 'react-router-dom';
import { AnimatedCounter } from '../../components';
import './Landing.scss';

export const Landing: React.FC = () => {
  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">
            <span className="pulse"></span>
            Now with AI Studio
          </div>
          <h1 className="hero-title">
            Modular Software
            <span className="gradient">For Growing Businesses</span>
          </h1>
          <p className="hero-subtitle">
            FLVR SOFT delivers powerful, modular software solutions designed specifically for SMEs. 
            Start with what you need, scale as you grow.
          </p>
          <div className="hero-cta">
            <Link to="/modules" className="glass-button primary">
              Explore Modules
            </Link>
            <Link to="/pricing" className="glass-button">
              View Pricing
            </Link>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-value">
                <AnimatedCounter end={50} suffix="K+" duration={2000} />
              </span>
              <span className="stat-label">Active Users</span>
            </div>
            <div className="stat">
              <span className="stat-value">
                <AnimatedCounter end={8} duration={1500} />
              </span>
              <span className="stat-label">Core Modules</span>
            </div>
            <div className="stat">
              <span className="stat-value">
                <AnimatedCounter end={99.9} suffix="%" decimals={1} duration={2500} />
              </span>
              <span className="stat-label">Uptime</span>
            </div>
          </div>
        </div>
        <div className="hero-visual">
          <div className="glass-card demo-card">
            <div className="demo-header">
              <div className="demo-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span className="demo-title">FLVR Dashboard</span>
            </div>
            <div className="demo-content">
              <div className="demo-sidebar">
                <div className="demo-icon active"></div>
                <div className="demo-icon"></div>
                <div className="demo-icon"></div>
                <div className="demo-icon"></div>
              </div>
              <div className="demo-main">
                <div className="demo-chart"></div>
                <div className="demo-widgets">
                  <div className="demo-widget"></div>
                  <div className="demo-widget"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="features">
        <h2 className="section-title">Everything You Need</h2>
        <p className="section-subtitle">Powerful tools that work together seamlessly</p>
        
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon accounting">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20M2 12h20M17 7l-5-5-5 5M17 17l-5 5-5-5"/>
              </svg>
            </div>
            <h3>Accounting</h3>
            <p>Full-suite accounting with invoicing, payroll, and tax compliance</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon pos">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="3" width="20" height="14" rx="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
            </div>
            <h3>Point of Sale</h3>
            <p>Modern POS system with inventory management and analytics</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon ecommerce">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1"/>
                <circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
            </div>
            <h3>E-Commerce</h3>
            <p>Launch your online store with integrated payment processing</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon website">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <line x1="3" y1="9" x2="21" y2="9"/>
                <line x1="9" y1="21" x2="9" y2="9"/>
              </svg>
            </div>
            <h3>Website Builder</h3>
            <p>Create stunning websites with our drag-and-drop builder</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon social">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
              </svg>
            </div>
            <h3>Social Media</h3>
            <p>Schedule, manage, and analyze all your social channels</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon ai">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"/>
                <path d="M8.5 8.5v.01M16 15v.01M12 12v.01M11 17v.01M7 14v.01"/>
              </svg>
            </div>
            <h3>AI Studio</h3>
            <p>Generate content, images, and code with AI-powered tools</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="glass-card cta-card">
          <h2>Ready to Transform Your Business?</h2>
          <p>Join 50,000+ businesses already using FLVR SOFT</p>
          <Link to="/contact" className="glass-button primary">
            Get Started Free
          </Link>
        </div>
      </section>
    </div>
  );
};
